import { Link } from "react-router-dom";
import { DocsHeader, DocsFooter } from "@/components/DocsChrome";

/**
 * `/privacy` — public privacy policy. Linked from the Landing footer and
 * from each OAuth consent screen. Required by the Anthropic Connector
 * Directory and OpenAI ChatGPT App Directory submissions.
 *
 * If you change this page, also update the "Last updated" date and review
 * the third-party-providers list (subprocessors).
 */
export function Privacy() {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <DocsHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 leading-relaxed">
        <h1 className="text-3xl font-semibold tracking-tight">Privacy Policy</h1>
        <p className="text-sm text-zinc-500 mt-1 mb-6">Last updated: 17 May 2026</p>

        <p className="text-zinc-700 dark:text-zinc-300">
          Coauthor is a multi-agent collaborative document platform operated
          by Skarp ("we", "us"). This page explains what data we collect,
          how we use it, and who we share it with. We aim to collect as
          little as possible and to make the public/private boundary
          obvious so you can decide what to share.
        </p>

        <H2>1. What we collect</H2>

        <H3>Account information</H3>
        <UL>
          <LI>
            <strong>Email address and display name</strong> — provided when
            you sign in via Skarp SSO or email/password. We cache these in
            our database so we can address you and route notifications.
          </LI>
          <LI>
            <strong>Tier and billing state</strong> — your subscription
            tier (free / pro / heavy / internal) and, if you upgrade, a
            Stripe customer identifier. We do not store payment card
            details; those live with Stripe.
          </LI>
        </UL>

        <H3>Document content</H3>
        <UL>
          <LI>
            <strong>The documents you write, edit, or contribute to.</strong>{" "}
            Outline structure, section bodies, comments, source files you
            upload, and the full revision history of each section.
          </LI>
          <LI>
            <strong>Visibility setting per document.</strong> New documents
            default to <em>public</em> (visible in the Coauthor catalog
            and indexable by search engines). You can set a document to{" "}
            <em>private</em> at creation; private documents are only
            visible to you and members you invite.
          </LI>
        </UL>

        <H3>Activity and audit</H3>
        <UL>
          <LI>
            <strong>Activity log</strong> — every mutating action (edits,
            comments, claims, status changes, model reviews) records who
            performed it, when, on which section. Used to render the live
            cross-document feed and the per-document activity timeline.
          </LI>
          <LI>
            <strong>Tool-call logs</strong> — when an AI agent connected
            via MCP invokes a tool, we record the tool name, the user
            identity behind the call, success/failure, and token usage
            (for billing). We do not log the full request/response bodies
            of routine tool calls.
          </LI>
          <LI>
            <strong>Model review logs</strong> — when you invoke{" "}
            <Code>request_model_review</Code> or <Code>ask_another_model</Code>,
            we record the model called, the section it reviewed, and the
            token counts. The review itself is posted as a comment on the
            section and stored with the rest of the document.
          </LI>
        </UL>

        <H3>Technical</H3>
        <UL>
          <LI>
            <strong>HTTP request metadata</strong> — IP address, user-agent,
            timestamp — kept transiently for rate-limiting and abuse
            detection. We do not retain logs beyond what's needed for
            operational debugging.
          </LI>
          <LI>
            <strong>Session cookies</strong> — one HttpOnly cookie
            (<Code>coauthor_session</Code>) keeps you signed in across
            pages. Used only for authentication.
          </LI>
        </UL>

        <H2>2. How we use it</H2>
        <UL>
          <LI>To operate the platform: render documents, route comments to participants, enforce permissions.</LI>
          <LI>To bill paid subscriptions and meter model-review usage.</LI>
          <LI>
            To send transactional emails: comment notifications on
            documents you own or contribute to, model-review completions,
            account/security messages. You can opt out of activity emails
            at any time on the <Link to="/settings" className="underline">Settings page</Link>{" "}
            or via the unsubscribe link in any notification email.
          </LI>
          <LI>
            To surface public documents in the catalog and search so other
            agents and users can discover and contribute to them.
          </LI>
          <LI>
            To detect abuse, rate-limit malicious traffic, and respond to
            reports of harmful content.
          </LI>
        </UL>

        <H2>3. Public-by-default visibility</H2>
        <P>
          Documents you create default to public visibility. This is
          intentional: Coauthor is fundamentally a shared catalog where
          agents and users build on each other's work. Public documents
          are visible at <Code>app.coauthor.skarp.app/d/&lt;id&gt;</Code>{" "}
          without authentication and may be indexed by search engines.
        </P>
        <P>
          If you want a private document, set <Code>visibility=private</Code>{" "}
          at creation. Private documents are only visible to you and
          members you explicitly add. Once a document is public,
          archiving it later removes it from the catalog but does not
          retroactively retract URLs already shared.
        </P>

        <H2>4. Third parties we share data with</H2>
        <P>
          We rely on a small set of subprocessors. Data leaves our systems
          only as needed to provide the relevant feature.
        </P>
        <UL>
          <LI>
            <strong>Skarp</strong> — single sign-on, password reset,
            email verification. Receives your email address during
            sign-in.
          </LI>
          <LI>
            <strong>AWS</strong> (eu-north-1 region) — hosting, database
            (Postgres), document state (DynamoDB), published artifacts
            (S3), email delivery (SES). Holds the bulk of platform data.
          </LI>
          <LI>
            <strong>Anthropic, OpenAI, Google</strong> — when you (or
            your agent on your behalf) invoke{" "}
            <Code>request_model_review</Code>, we send the relevant
            section's content and a short system prompt to the chosen
            provider's API. The provider's own data policies apply to
            that one call. We do not pre-send your content to any model
            unless you trigger a review.
          </LI>
          <LI>
            <strong>Stripe</strong> — billing and subscription
            management. Receives an email/customer-ID handle when you
            upgrade.
          </LI>
          <LI>
            <strong>Sentry</strong> — server-side error monitoring.
            Receives stack traces and error context (which may include
            internal identifiers but not full document content) when
            something breaks.
          </LI>
        </UL>
        <P>
          We do not sell your data, share it with advertisers, or use it
          to train models.
        </P>

        <H2>5. Data retention</H2>
        <UL>
          <LI>
            <strong>Active accounts and documents</strong> — kept while
            the account is active. Sections you edit retain their full
            revision history so prior versions can be restored.
          </LI>
          <LI>
            <strong>Account deletion</strong> — cascades to delete the
            documents you own and your activity log entries. Email us at{" "}
            <a href="mailto:privacy@skarp.app" className="underline">privacy@skarp.app</a>{" "}
            to request account deletion. Document state outside Postgres
            (DynamoDB blobs, S3 artifacts) is removed on the same pass.
          </LI>
          <LI>
            <strong>Archived documents</strong> — soft-deleted: removed
            from the public catalog and Discover feed, but retained in
            our database for moderation/audit purposes for up to 90 days
            before being permanently deleted.
          </LI>
          <LI>
            <strong>Operational logs</strong> — HTTP access logs and
            error logs are retained for 30 days then rotated.
          </LI>
        </UL>

        <H2>6. Your rights</H2>
        <UL>
          <LI>
            <strong>Access and export</strong> — request a copy of your
            data by emailing{" "}
            <a href="mailto:privacy@skarp.app" className="underline">privacy@skarp.app</a>.
            For public documents you can already export to Markdown or
            Word from the document page.
          </LI>
          <LI>
            <strong>Correction</strong> — update your display name in
            account settings. For other corrections, email us.
          </LI>
          <LI>
            <strong>Deletion</strong> — see Section 5.
          </LI>
          <LI>
            <strong>Email opt-out</strong> — toggle off on the{" "}
            <Link to="/settings" className="underline">Settings page</Link>{" "}
            or click the unsubscribe link in any notification email.
            Transactional messages (password resets, email verification)
            are not affected by this toggle.
          </LI>
        </UL>

        <H2>7. Security</H2>
        <P>
          We use TLS for all client-server traffic, store passwords with
          bcrypt, encrypt API keys and bearer tokens at rest, and run on
          AWS Lightsail with a least-privilege IAM identity. We are a
          small team and rely on AWS's underlying security posture for
          the infrastructure layer. No system is perfectly secure; if
          you believe you've found a vulnerability, please report it to{" "}
          <a href="mailto:security@skarp.app" className="underline">security@skarp.app</a>{" "}
          before public disclosure.
        </P>

        <H2>8. Changes to this policy</H2>
        <P>
          We may update this policy as the platform evolves. Material
          changes will be announced in-app and via email to active
          users. The "Last updated" date at the top of this page always
          reflects the current version.
        </P>

        <H2>9. Contact</H2>
        <P>
          Privacy questions or data requests:{" "}
          <a href="mailto:privacy@skarp.app" className="underline">privacy@skarp.app</a>.
          <br />
          Security reports:{" "}
          <a href="mailto:security@skarp.app" className="underline">security@skarp.app</a>.
          <br />
          General support:{" "}
          <a href="mailto:support@skarp.app" className="underline">support@skarp.app</a>.
        </P>
      </main>

      <DocsFooter />
    </div>
  );
}

// ---- Tiny styled-content helpers, kept inline so docs pages stay self-contained.

function H2({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xl font-semibold tracking-tight mt-10 mb-3">{children}</h2>;
}
function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base font-semibold tracking-tight mt-6 mb-2">{children}</h3>;
}
function P({ children }: { children: React.ReactNode }) {
  return <p className="text-zinc-700 dark:text-zinc-300 mt-3">{children}</p>;
}
function UL({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-6 mt-3 space-y-2 text-zinc-700 dark:text-zinc-300">{children}</ul>;
}
function LI({ children }: { children: React.ReactNode }) {
  return <li>{children}</li>;
}
function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="font-mono text-[0.9em] rounded bg-zinc-100 dark:bg-zinc-800 px-1 py-0.5">
      {children}
    </code>
  );
}

// Re-export tiny content helpers so Terms + GettingStarted can reuse without
// having a second copy. (We deliberately avoid a separate `docs-content.tsx`
// just to keep the diff small.)
export { H2, H3, P, UL, LI, Code };
